import { Label } from '@/components/ui/Label'
import { cn } from '@/lib/utils'
import type { HelpBannerTone } from '@/pages/HelpCenter/types'

interface BannerTonePickerProps {
  tone: HelpBannerTone
  accentColor: string | null
  brandPrimary: string | null
  onToneChange: (tone: HelpBannerTone) => void
  onAccentChange: (color: string | null) => void
}

// Swatch colours mirror the .hc-banner--* rules in help_center.css. 'brand'
// has no fixed colour — it follows the help center's own primary, so it is
// previewed with the account's brand colour when we know it.
const TONES: { value: HelpBannerTone; label: string; swatch: string | null }[] = [
  { value: 'brand', label: 'Brand', swatch: null },
  { value: 'neutral', label: 'Neutral', swatch: '#8b8b8b' },
  { value: 'info', label: 'Info', swatch: '#3b82f6' },
  { value: 'success', label: 'Success', swatch: '#10b981' },
  { value: 'warning', label: 'Warning', swatch: '#f59e0b' },
  { value: 'danger', label: 'Danger', swatch: '#ef4444' },
]

export function BannerTonePicker({ tone, accentColor, brandPrimary, onToneChange, onAccentChange }: BannerTonePickerProps) {
  const custom = Boolean(accentColor)

  return (
    <div className="space-y-2">
      <Label>Colour</Label>
      <div className="flex flex-wrap items-center gap-2">
        {TONES.map(({ value, label, swatch }) => (
          <button
            key={value}
            type="button"
            onClick={() => { onToneChange(value); onAccentChange(null) }}
            aria-pressed={!custom && tone === value}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
              !custom && tone === value ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            <span
              className="size-3 rounded-full border border-border/60"
              style={{ background: swatch ?? brandPrimary ?? 'var(--primary)' }}
            />
            {label}
          </button>
        ))}

        <label
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
            custom ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          <input
            type="color"
            className="size-3 cursor-pointer appearance-none border-0 bg-transparent p-0"
            value={accentColor ?? brandPrimary ?? '#10b981'}
            onChange={e => onAccentChange(e.target.value)}
          />
          Custom
        </label>

        {custom && (
          <button
            type="button"
            onClick={() => onAccentChange(null)}
            className="cursor-pointer text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Banners are tinted, not filled — the colour shows as a left edge and a faint wash of the page surface.
      </p>
    </div>
  )
}
